import {ChatServerMsg, TYPES} from "../libs/config";
import {typeToHR} from "../libs/ScriptHelper";
import {log} from "../libs/QuerloLogger";

export default class ChatQueue {

    /** stores messages received from java server and not yet rendered */
    private queue: ChatServerMsg[] = [];

    /** sent to viewport through specific callback, stores all messages which must be rendered */
    private messagesRenderList: ChatServerMsg[] = [];

    private readonly updateStateCallback: any;
    private currentHandledMessage: ChatServerMsg | null;

    private _globalChatCfg: any;

  constructor(actionListener: any, globalCfg: any) {
        this.updateStateCallback = actionListener;
        this._globalChatCfg = globalCfg;
        this.currentHandledMessage = null;
    }

    public push(msg: ChatServerMsg): void {
        this.queue.push(msg);
        log.debug(`##_QUEUE_## Queued msg id: ${msg.id} (${typeToHR(msg.t)}), isIdle: ${this.isIdle()}, Internal queue length: ${this.queue.length}`);
        /** auto advance queue when queue was idle */
        if (this.isIdle()) {
            this.tick();
        }
    }

    public tick(): void {
        log.debug(`##_QUEUE_## TICK Internal queue length: ${this.queue.length}${this.queue.length === 0 ? " * NOOP" : ""}`);
        if (this.currentHandledMessage && this.currentHandledMessage.t === TYPES.USER_TYPING) {
          if (this.currentHandledMessage.b.delay > -1 || this.queue.length) {
            /** we immediately remove standard (auto hiding) typing messages or INTERRUPTABLE typing messages when some messages are already scheduled after them **/
            const removableMessage = this.messagesRenderList[this.messagesRenderList.length - 1];
            log.debug(`##_QUEUE_##  removing already rendered TYPING msg from render list, id: ${removableMessage.id} (${typeToHR(removableMessage.t)}), Internal queue length: ${this.queue.length}`);
            this.messagesRenderList.splice(-1, 1);
          } else {
            /** if delay === -1, we have an INTERRUPTABLE typing message, which lasts forever, until next message kicks in,
             * so we won't remove it now and neither we will set currentHandledMessage to null. isIdle() method now returns true also when there is an
             * INTERRUPTABLE typing message waiting **/
            return;
          }
        }

        if (!this.queue.length) {
            /** no more messages to handle at this time, we will be waiting for next msg from server */
            this.currentHandledMessage = null;
            return;
        }

        this.moveNextAvailableQueuedMessageToRenderList();
        this.compileExtraMessageField(this.currentHandledMessage as ChatServerMsg, this.getPreviousRenderedMessage());

        /** this will make App update chat viewport */
        log.debug(`##_QUEUE_## added msg id: ${(this.currentHandledMessage as any).id} (${typeToHR((this.currentHandledMessage as any).t)}) to render list, triggering state update. Internal queue length: ${this.queue.length}`);
        this.updateStateCallback(this.messagesRenderList);
    }

    private moveNextAvailableQueuedMessageToRenderList() {
        /** current message being rendered, reset (and updated) on every tick()
         * (when null, queue is considered idle and will automatically restart when next message arrives) */
        this.currentHandledMessage = this.queue.shift() as ChatServerMsg;
        /** remove first message from queue and put it on render list */
        this.messagesRenderList.push(this.currentHandledMessage as ChatServerMsg);
    }

    private isIdle() {
    /** no message being handled or INTERRUPTABLE TYPING MESSAGE **/
        return (this.currentHandledMessage === null || (this.currentHandledMessage?.t === TYPES.USER_TYPING && this.currentHandledMessage?.b.delay === -1));
    }

    /**
     * dynamically compiles me, merged, avatar1 and avatar2 fields which are not handled by chat server
     * merged means that a messages will be rendered without the avatar and without vertical space between the message above
     *
     * @param currentHandledMessage
     * @param lastRenderedMessage
     * @private
     */
    private compileExtraMessageField(currentHandledMessage: ChatServerMsg, lastRenderedMessage: null | ChatServerMsg): void {
      /** it looks like chat server only sends .u field for POLL_ANSWER type */
      currentHandledMessage.me =
        currentHandledMessage.spk === 2 ||
        (typeof currentHandledMessage.u !== "undefined" &&
        this._globalChatCfg.user.id === currentHandledMessage.u);

      if (currentHandledMessage.me) {
        /** msg sent out by user */
        currentHandledMessage.avatar2 =
          this._globalChatCfg.AVATAR2 &&
          currentHandledMessage.t !== TYPES.USER_TYPING
            ? this._globalChatCfg.AVATAR2
            : "";
        currentHandledMessage.avatar1 = "";
        currentHandledMessage.name = this._globalChatCfg.NICK2 ?? "";
      } else {
        /** msg received by bot or agent */
        currentHandledMessage.avatar1 =
          this._globalChatCfg.AVATAR1 &&
          !currentHandledMessage.merged &&
          // currentHandledMessage.t !== TYPES.USER_TYPING &&
          currentHandledMessage.t !== TYPES.HUMAN_TAKEOVER
            ? this._globalChatCfg.AVATAR1
            : "";
        currentHandledMessage.avatar2 = "";
        currentHandledMessage.name = this._globalChatCfg.NICK1 ?? "";
      }

      currentHandledMessage.merged =
        lastRenderedMessage !== null &&
        lastRenderedMessage.t !== TYPES.HUMAN_TAKEOVER &&
        lastRenderedMessage.spk === currentHandledMessage.spk &&
        // currentHandledMessage.t !== TYPES.SEND_POLL &&
        !currentHandledMessage.ls &&
        currentHandledMessage.b &&
        !currentHandledMessage.b.ls;
    }

    private getPreviousRenderedMessage(): ChatServerMsg | null {

        for (let i = this.messagesRenderList.length - 2; i >= 0; i--) {
            if (this.messagesRenderList[i].isViewPortMessage !== false) {
                return this.messagesRenderList[i];
            }
        }

        return null;
        // return this.messagesRenderList.length > 1 ? this.messagesRenderList[this.messagesRenderList.length - 2] : null;
    }

    private getLastQueuedMessage(): ChatServerMsg | null {
    if (!this.queue.length) {
      return null;
    }
    return this.queue[this.queue.length-1]
  }

    clearChatMessages(): void {
        this.messagesRenderList = [];
        this.updateStateCallback(this.messagesRenderList);
    }

    clearQueuedMessages(): void {
      log.debug(`##_QUEUE_## CLEAR, discarding ${this.queue.length} messages already queued`);
      this.queue = [];
    }

    isTypingCurrentlyRenderedOrLastScheduled(): boolean {
      return this.getLastQueuedMessage()?.t === TYPES.USER_TYPING || this.currentHandledMessage?.t === TYPES.USER_TYPING;
    }

}


